import axios from 'axios';

/**
 * Axios-Client mit automatischem Token-Handling.
 *
 * SICHERHEITSREGEL: Access-Token wird NUR im Memory gehalten (Variable).
 * Kein localStorage! Refresh-Token kommt als HttpOnly Cookie.
 */

// Access-Token im Memory (nicht localStorage!)
let accessToken: string | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const getAccessToken = (): string | null => {
  return accessToken;
};

// Refresh-Lock: verhindert parallele Refresh-Anfragen (Race Condition)
let refreshPromise: Promise<string> | null = null;

// API-Client
const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true, // HttpOnly Cookie mitsenden
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request-Interceptor: Access-Token anfuegen
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Response-Interceptor: Bei 401 automatisch Token erneuern (Silent Refresh)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Bei 401 und noch kein Retry versucht
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== '/auth/refresh' &&
      originalRequest.url !== '/auth/login'
    ) {
      originalRequest._retry = true;

      try {
        // Refresh-Lock: alle parallelen 401-Fehler teilen denselben Refresh-Aufruf
        if (!refreshPromise) {
          refreshPromise = axios
            .post<{ accessToken: string }>(
              '/api/v1/auth/refresh',
              {},
              { withCredentials: true },
            )
            .then((res) => {
              setAccessToken(res.data.accessToken);
              return res.data.accessToken;
            })
            .catch((err) => {
              setAccessToken(null);
              window.dispatchEvent(new CustomEvent('auth:unauthorized'));
              throw err;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        const newToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
